import { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import { useStore } from '../services/store';
import AmoxtliIcon from './AmoxtliIcon';

const AIHelpChat = ({ isOpen, onClose, initialInput = '' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [escalationStatus, setEscalationStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const { user } = useStore();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Mensaje inicial del bot
      setMessages([
        {
          id: 1,
          type: 'bot',
          text: '¡Hola! 👋 Soy el asistente de IA del SAT System. ¿En qué puedo ayudarte? Si mi respuesta no resuelve tu problema, puedo escalarlo a un agente humano.',
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialInput) {
      setInput(initialInput);
    }
  }, [isOpen, initialInput]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.post('/api/ai-help/chat', {
        message: input,
        conversation_history: messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.text
        }))
      });

      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: response.data.response,
        timestamp: new Date(),
        escalation_suggested: response.data.escalation_suggested,
        escalation_reason: response.data.escalation_reason,
        incident_type: response.data.incident_type
      };

      setMessages(prev => [...prev, botMessage]);

      // Si se sugiere escalado, mostrar opción
      if (response.data.escalation_suggested) {
        setEscalationStatus({
          suggested: true,
          reason: response.data.escalation_reason,
          type: response.data.incident_type
        });
      }
    } catch (error) {
      console.error('Error in AI chat:', error);
      const errorMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: 'Lo siento, hubo un error procesando tu pregunta. Por favor, intenta de nuevo.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalateToEmail = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/ai-help/escalate-email', {
        user_email: user.email,
        user_name: user.nombre_completo,
        conversation: messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.text
        })),
        escalation_reason: escalationStatus.reason,
        incident_type: escalationStatus.type
      });

      const botMessage = {
        id: messages.length + 1,
        type: 'bot',
        text: `✉️ Tu solicitud ha sido enviada por correo al equipo de soporte. Recibirás una respuesta en las próximas 24 horas a ${user.email}.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setEscalationStatus(null);
      
      setTimeout(() => {
        const resetMessage = {
          id: messages.length + 2,
          type: 'bot',
          text: '¿Hay algo más en lo que pueda ayudarte?',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resetMessage]);
      }, 2000);
    } catch (error) {
      console.error('Error escalating to email:', error);
      const errorMessage = {
        id: messages.length + 1,
        type: 'bot',
        text: 'No pudimos procesar tu escalación. Por favor intenta de nuevo.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalateToAgent = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/ai-help/escalate-agent', {
        user_email: user.email,
        user_name: user.nombre_completo,
        conversation: messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.text
        })),
        escalation_reason: escalationStatus.reason,
        incident_type: escalationStatus.type
      });

      const botMessage = {
        id: messages.length + 1,
        type: 'bot',
        text: `🎧 Tu caso ha sido escalado a un agente humano. Tiket #${response.data.ticket_id}. Un agente estará disponible dentro de los próximos 30 minutos.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setEscalationStatus(null);
      
      setTimeout(() => {
        const resetMessage = {
          id: messages.length + 2,
          type: 'bot',
          text: '¿Hay algo más en lo que pueda ayudarte mientras esperas?',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, resetMessage]);
      }, 2000);
    } catch (error) {
      console.error('Error escalating to agent:', error);
      const errorMessage = {
        id: messages.length + 1,
        type: 'bot',
        text: 'No pudimos procesar tu escalación a agente. Por favor intenta de nuevo.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col border border-gray-300 z-40">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AmoxtliIcon variant="stamp" className="h-6 w-6" alt="Sello fiscal Amoxtli" />
          <div>
            <h3 className="font-semibold">Asistente IA</h3>
            <p className="text-xs text-blue-100">Siempre disponible</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="hover:bg-blue-500 p-1 rounded transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-xs px-4 py-2 rounded-lg ${
                message.type === 'user'
                  ? 'bg-blue-600 text-white rounded-br-none'
                  : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
              }`}
            >
              <p className="text-sm">{message.text}</p>
              <span className="text-xs opacity-70 mt-1 block">
                {message.timestamp.toLocaleTimeString('es-MX', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        ))}

        {/* Escalation Options */}
        {escalationStatus?.suggested && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
            <p className="text-sm text-amber-900 font-medium">
              ⚠️ Parece que necesitas ayuda adicional
            </p>
            <p className="text-xs text-amber-800 mb-3">
              Razón: {escalationStatus.reason}
            </p>
            <div className="flex gap-2 flex-col">
              <button
                onClick={handleEscalateToEmail}
                disabled={isLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white text-xs py-2 px-3 rounded transition-colors"
              >
                ✉️ Enviar ticket por correo
              </button>
              <button
                onClick={handleEscalateToAgent}
                disabled={isLoading}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-xs py-2 px-3 rounded transition-colors"
              >
                🎧 Hablar con agente humano
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            placeholder="Escribe tu pregunta..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {isLoading ? '...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AIHelpChat;
